/* =============================================================================
   Farmoxa Premium — product page: content tabs, media controls (360 / zoom / video),
   mobile sticky buy bar. Works with Dawn's media-gallery + product-info elements.
   ============================================================================= */
(() => {
  'use strict';
  const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ----------------------------------------------------------------- tabs */
  const tabNav = document.querySelector('[data-fmx-pdp-tabs]');
  if (tabNav) {
    const tabs = Array.from(tabNav.querySelectorAll('[data-fmx-tab]'));
    const targets = tabs.map((t) => document.getElementById(t.dataset.fmxTab)).filter(Boolean);
    function activate(id) {
      tabs.forEach((t) => t.setAttribute('aria-selected', t.dataset.fmxTab === id ? 'true' : 'false'));
    }
    tabs.forEach((t) => {
      t.addEventListener('click', (e) => {
        e.preventDefault();
        const el = document.getElementById(t.dataset.fmxTab);
        if (!el) return;
        activate(t.dataset.fmxTab);
        el.scrollIntoView({ behavior: mqReduce.matches ? 'auto' : 'smooth', block: 'start' });
        el.setAttribute('tabindex', '-1');
        el.focus({ preventScroll: true });
      });
    });
    if ('IntersectionObserver' in window && targets.length) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => { if (en.isIntersecting) activate(en.target.id); });
      }, { rootMargin: '-45% 0px -50% 0px', threshold: 0 });
      targets.forEach((el) => io.observe(el));
    }
  }

  /* -------------------------------------------------------- media controls */
  const controls = document.querySelector('[data-fmx-media-controls]');
  const gallery = document.querySelector('media-gallery');
  if (controls && gallery) {
    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    const thumbs = gallery.querySelector('[id^="GalleryThumbnails"]');
    function activeSlide() { return viewer?.querySelector('.product__media-item.is-active'); }
    function activateMedia(li) {
      if (!li) return;
      const btn = thumbs?.querySelector(`[data-target="${li.dataset.mediaId}"] button`);
      if (btn) btn.click();
      else if (gallery.setActiveMedia) gallery.setActiveMedia(li.dataset.mediaId, false);
      return li;
    }
    controls.querySelector('[data-fmx-zoom]')?.addEventListener('click', () => {
      const li = activeSlide();
      const opener = li?.querySelector('.product__media-toggle') || li?.querySelector('.product__modal-opener button');
      if (opener) opener.click();
    });
    controls.querySelector('[data-fmx-video]')?.addEventListener('click', () => {
      const li = Array.from(viewer.querySelectorAll('.product__media-item')).find((l) => l.querySelector('deferred-media:not(product-model)'));
      if (!li) return;
      activateMedia(li);
      setTimeout(() => li.querySelector('.deferred-media__poster')?.click(), 60);
    });
    controls.querySelector('[data-fmx-360]')?.addEventListener('click', () => {
      const li = Array.from(viewer.querySelectorAll('.product__media-item')).find((l) => l.querySelector('product-model'));
      if (!li) return;
      activateMedia(li);
      setTimeout(() => li.querySelector('.deferred-media__poster')?.click(), 60);
    });
  }

  /* ------------------------------------------- vertical thumbnail column */
  // Dawn's thumbnail slider only knows horizontal scrolling; on desktop the thumbnails form a scrollable column next
  // to the main image, so keep the active thumbnail in view and show a fade while more thumbnails are below.
  const thumbSlider = document.querySelector('media-gallery [id^="GalleryThumbnails"]');
  const thumbList = thumbSlider?.querySelector('.thumbnail-list');
  if (thumbSlider && thumbList) {
    const vertical = () => getComputedStyle(thumbList).flexDirection === 'column' && thumbList.scrollHeight > thumbList.clientHeight + 2;
    const indicate = () => {
      const more = vertical() && thumbList.scrollTop + thumbList.clientHeight < thumbList.scrollHeight - 2;
      thumbSlider.dataset.fmxScroll = more ? 'more' : 'end';
    };
    const reveal = (btn) => {
      if (!vertical()) return;
      const item = btn.closest('.thumbnail-list__item');
      if (!item) return;
      const top = item.offsetTop - 2;
      const bottom = item.offsetTop + item.offsetHeight + 2;
      const behavior = mqReduce.matches ? 'auto' : 'smooth';
      if (top < thumbList.scrollTop) thumbList.scrollTo({ top, behavior });
      else if (bottom > thumbList.scrollTop + thumbList.clientHeight) thumbList.scrollTo({ top: bottom - thumbList.clientHeight, behavior });
    };
    new MutationObserver((muts) => {
      muts.forEach((m) => { if (m.target.getAttribute('aria-current') === 'true') reveal(m.target); });
    }).observe(thumbList, { attributes: true, attributeFilter: ['aria-current'], subtree: true });
    thumbList.addEventListener('scroll', indicate, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(indicate).observe(thumbList);
    window.addEventListener('load', indicate);
    indicate();
    const current = thumbList.querySelector('[aria-current="true"]');
    if (current) reveal(current);
  }

  /* ---------------------------------------------------------- sticky buy */
  const sticky = document.querySelector('[data-fmx-sticky-buy]');
  const submit = document.querySelector('.product-form__submit');
  if (sticky && submit && 'IntersectionObserver' in window) {
    document.body.classList.add('fmx-has-sticky-buy');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => sticky.classList.toggle('is-visible', !en.isIntersecting && en.boundingClientRect.top < 0));
    }, { threshold: 0 });
    io.observe(submit);
    sticky.querySelector('button')?.addEventListener('click', () => {
      submit.scrollIntoView({ behavior: mqReduce.matches ? 'auto' : 'smooth', block: 'center' });
      setTimeout(() => submit.focus(), 400);
    });
    // keep the sticky price in sync with Dawn's price re-render
    const price = document.querySelector('[id^="price-"]');
    const out = sticky.querySelector('[data-fmx-sticky-price]');
    if (price && out) {
      const sync = () => { const p = price.querySelector('.price-item--sale.price-item--last, .price-item--regular'); if (p) out.textContent = p.textContent.trim(); };
      new MutationObserver(sync).observe(price, { childList: true, subtree: true, characterData: true });
      sync();
    }
  }

  /* ------------------------------------------------- collapsible description */
  document.querySelectorAll('[data-fmx-desc]').forEach((box) => {
    const toggle = box.parentElement.querySelector('[data-fmx-desc-toggle]');
    const inner = box.querySelector('.fmx-desc__in');
    if (!toggle || !inner) return;
    const max = parseInt(getComputedStyle(box).getPropertyValue('--fmx-desc-max'), 10) || 360;
    const apply = () => {
      const tall = inner.scrollHeight > max + 60;
      toggle.hidden = !tall;
      if (!tall) box.classList.remove('is-collapsed');
      else if (toggle.getAttribute('aria-expanded') !== 'true') box.classList.add('is-collapsed');
    };
    toggle.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
      box.classList.toggle('is-collapsed', open);
      toggle.firstChild.textContent = open ? toggle.dataset.more : toggle.dataset.less;
      if (open && box.getBoundingClientRect().top < 0) box.scrollIntoView({ behavior: mqReduce.matches ? 'auto' : 'smooth', block: 'start' });
    });
    apply();
    window.addEventListener('resize', apply);
  });
})();
